/**
 * Watches for inactivity while a person is signed in and calls `onLock` after the interval the policy sets for
 * this workstation's class (SEC-014, URS-11.7-007). Renders nothing; see lib/idle-lock.ts for the why.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { endpoints } from "../lib/api.js";
import {
  CLASS_CHANGED,
  createIdleLock,
  FALLBACK_POLICY,
  lockMs,
  workstationClass,
} from "../lib/idle-lock.js";

const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"] as const;

export function IdleGuard({ onLock }: { onLock: () => void }) {
  const policy = useQuery({
    queryKey: ["session-policy"],
    queryFn: () => endpoints.sessionPolicy(),
    staleTime: 300_000,
    retry: false,
  });
  const [cls, setCls] = useState(() => workstationClass(localStorage));
  useEffect(() => {
    const changed = () => setCls(workstationClass(localStorage));
    window.addEventListener(CLASS_CHANGED, changed);
    return () => window.removeEventListener(CLASS_CHANGED, changed);
  }, []);

  // unreadable policy: lock on the fallback (short) interval rather than never
  const resolved = policy.data ?? (policy.isError ? FALLBACK_POLICY : null);
  const idleMs = resolved ? lockMs(resolved, cls) : null;

  useEffect(() => {
    if (idleMs === null) return;
    const lock = createIdleLock({
      idleMs,
      onLock,
      now: () => Date.now(),
      setTimer: (fn, ms) => window.setTimeout(fn, ms),
      clearTimer: (h) => window.clearTimeout(h as number),
    });
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, lock.touch, { passive: true, capture: true });
    return () => {
      lock.stop();
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, lock.touch, { capture: true });
    };
  }, [idleMs, onLock]);

  return null;
}
